import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const API_URL = '';

export default function PortalMediaPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useBusinessAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    image_url: '',
    image_credit: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/partner/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchBusiness();
    }
  }, [user]);

  const fetchBusiness = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/business`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          image_url: data.image_url || '',
          image_credit: data.image_credit || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/partner/portal/business`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Image updated successfully');
        await refreshUser();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update image');
      }
    } catch (error) {
      toast.error('Failed to update image');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-media-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link 
            to="/portal"
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900 font-heading">
            Media
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="px-6 py-8">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Business Image</h2>
            
            {/* Current Image */}
            {formData.image_url ? (
              <div className="relative">
                <img
                  src={formData.image_url}
                  alt="Business"
                  className="w-full h-48 object-cover rounded-xl"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-48 bg-slate-100 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No image set</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="https://images.unsplash.com/..."
                className="h-12"
                data-testid="input-image-url"
              />
              <p className="text-xs text-slate-500">
                Paste a URL to an image. We recommend Unsplash for free, high-quality images.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_credit">Image Credit (optional)</Label>
              <Input
                id="image_credit"
                value={formData.image_credit}
                onChange={(e) => handleChange('image_credit', e.target.value)}
                placeholder="Unsplash / Photographer name"
                className="h-12"
                data-testid="input-image-credit"
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700"
            data-testid="save-media-btn"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
